import { effect, inject } from '@angular/core';
import type {
  CanActivateChildFn,
  CanActivateFn,
  GuardResult,
} from '@angular/router';
import { Router } from '@angular/router';

import { AuthenticationService } from '#/app/shared/services/auth/authentication.service';

/**
 * `authGuard` and `authGuardChild` ensure that routes are only accessible to authenticated users.
 * If the user is not authenticated or the identity provider is unknown,
 * they are redirected to the login page with a `returnUrl` query parameter.
 */
export const authGuard: CanActivateFn = (route, state) => {
  const authenticationService = inject(AuthenticationService);
  const router = inject(Router);

  return new Promise<GuardResult>((resolve) => {
    const effectRef = effect(
      () => {
        if (
          !authenticationService.isAuthLoading() &&
          !authenticationService.isRedirecting()
        ) {
          effectRef.destroy();

          const isAuthenticated = authenticationService.isAuthenticated();
          const currentIdentityProvider =
            authenticationService.currentIdentityProvider();
          if (!isAuthenticated || currentIdentityProvider === 'Unknown') {
            resolve(
              router.createUrlTree(['/auth', 'login'], {
                queryParams: { returnUrl: state.url },
              }),
            );
          } else {
            resolve(true);
          }
        } else {
          effectRef.destroy();
          resolve(
            router.createUrlTree(['/auth', 'login'], {
              queryParams: { returnUrl: state.url },
            }),
          );
        }
      },
      { manualCleanup: true },
    );
  });
};

export const authGuardChild: CanActivateChildFn = (route, state) => {
  return authGuard(route, state);
};
